import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useUserProfileInit } from 'stores/userProfileStore';
import {
  useUserPreferencesInit,
  useUserPreferencesStore,
} from 'stores/userPreferencesStore';
import { useActiveCompanyInit } from 'stores/activeCompanyStore';
import { ROUTES } from 'utils/constants';
import Membership from 'pages/Membership';
import AddMembership from 'pages/Membership/AddMembership';
import EditMembership from 'pages/Membership/EditMembership';
import MembershipHistory from 'pages/Membership/MembershipHistory';
import PendingInvitations from 'pages/Membership/PendingInvitations';
import RollbackMembership from 'pages/Membership/RollbackMembership';
import MembershipStatistics from 'pages/Membership/MembershipStatistics';
import ChurchReports from 'pages/churchreports/ChurchReports';
import churchReportsShellStyles from 'pages/churchreports/ChurchReports.module.scss';
import WebsiteBuilder from 'pages/Membership/WebsiteBuilder';
import WebsiteBuilderPageBuilder from 'pages/Membership/WebsiteBuilder/PageBuilder';
import PublicWebsitePage from 'pages/Membership/WebsiteBuilder/PublicWebsitePage';
import PublicWebsiteHomePage from 'pages/Membership/WebsiteBuilder/PublicWebsiteHomePage';
import Reports from 'pages/reports';
import InventoryReport from 'pages/reports/InventoryReport';
import SalesTransaction from 'pages/sales/SalesTransaction';
import Sales from 'pages/sales';
import ViewSales from 'pages/sales/ViewSales';
import Expenses from 'pages/expenses';
import ViewExpense from 'pages/expenses/ViewExpense';
import Dashboard from 'pages/dashboard';
import UserProfile from 'pages/user/Profile';
import UserSettings from 'pages/user/Settings';
import ChangePassword from 'pages/user/ChangePassword';
import CompleteProfile from 'pages/user/CompleteProfile';
import CompanyLobby from 'pages/company/Lobby/CompanyLobby';
import Products from 'pages/products';
import AddProduct from 'pages/products/Add';
import EditProduct from 'pages/products/Edit';
import ProductInventory from 'pages/products/ProductInventory';
import InventoryList from 'pages/inventory';
import AddInventory from 'pages/inventory/AddInventory';
import Customers from 'pages/customers';
import Suppliers from 'pages/suppliers';
import Companies from 'pages/companies';
import ApiKeys from 'pages/apiKeys';
import CompanyUsers from 'pages/company/Users';
import Permission from 'pages/permission';
import Role from 'pages/role';
import POS from 'pages/pos';
import Payment from 'pages/pos/Payment';
import Success from 'pages/pos/Success';
import POSLayout from 'pages/pos/POSLayout';
import PrinterSetup from 'pages/printerSetup/PrinterSetup';
import Header from 'components/Header';
import Sidebar from 'components/Sidebar';
import ProtectedRoute from 'components/ProtectedRoute';
import CompanyGuard from 'components/CompanyGuard';
import PrinterSettings from 'pages/pos/Settings/PrinterSettings';
import Kanban from 'pages/kanban/Kanban';
import Chat from 'pages/chat';
import Storage from 'pages/storage';
import Schedules from 'pages/schedules';
import SalesOrdersV2 from 'pages/v2/SalesOrdersV2';
import SalesV2 from 'pages/v2/SalesV2';
import PurchaseOrdersV2 from 'pages/v2/PurchaseOrdersV2';
import PurchasesV2 from 'pages/v2/PurchasesV2';
import NotFound from 'pages/not-found';
import Forms from 'pages/forms';
import Help from 'pages/help';
import HelpHowToTopic from 'pages/help/HelpHowToTopic';

function LegacyWebsiteBuilderPageRedirect() {
  const { pageId } = useParams();
  const target = ROUTES.MEMBERSHIP.WEBSITE_BUILDER_PAGE.replace(
    ':pageId',
    pageId ?? '',
  );
  return <Navigate to={target} replace />;
}

function AuthenticatedRoutes() {
  useUserProfileInit();
  useUserPreferencesInit();
  useActiveCompanyInit();
  const betaEnabled = useUserPreferencesStore((s) => s.betaEnabled);

  return (
    <ProtectedRoute>
      <Routes>
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Dashboard />
              </>
            </CompanyGuard>
          }
        />
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.STORAGE.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Storage />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.SCHEDULES.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Schedules />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.CHAT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Chat />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.FORMS}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Forms />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.HELP}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Help />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.HELP_HOW_TO}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <HelpHowToTopic />
              </>
            </CompanyGuard>
          }
        />
        <Route path={ROUTES.COMPANY.SETUP} element={<CompanyLobby />} />
        {betaEnabled && (
          <Route
            path={ROUTES.COMPANY.USERS}
            element={
              <CompanyGuard>
                <>
                  <Sidebar />
                  <Header />
                  <CompanyUsers />
                </>
              </CompanyGuard>
            }
          />
        )}
        <Route
          path={ROUTES.COMPANIES.ROOT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Companies />
              </>
            </CompanyGuard>
          }
        />
        {betaEnabled && (
          <Route
            path={ROUTES.API_KEYS}
            element={
              <CompanyGuard>
                <>
                  <Sidebar />
                  <Header />
                  <ApiKeys />
                </>
              </CompanyGuard>
            }
          />
        )}
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.MEMBERSHIP.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <div className={churchReportsShellStyles.container}>
                      <Membership
                        showUtilityButtons={false}
                        showTitle
                        titleText="General Member's List"
                        showBackButton
                        backButtonPath={ROUTES.CHURCH.REPORTS.ROOT}
                        alignContainerStart
                      />
                    </div>
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={`${ROUTES.CHURCH.REPORTS.ROOT}/*`}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <ChurchReports />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.ADD}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <AddMembership />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.EDIT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <EditMembership />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.HISTORY}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <MembershipHistory />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.VIEW_HISTORY}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <MembershipHistory />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.PENDING}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <PendingInvitations />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.ROLLBACK}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <RollbackMembership />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.WEBSITE_HOME}
              element={
                <CompanyGuard>
                  <PublicWebsiteHomePage />
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.WEBSITE_SLUG}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <PublicWebsitePage />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path="/membership/website-builder/page/:pageId"
              element={<LegacyWebsiteBuilderPageRedirect />}
            />
            <Route
              path={ROUTES.MEMBERSHIP.WEBSITE_BUILDER}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <WebsiteBuilder />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.WEBSITE_BUILDER_PAGE}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <WebsiteBuilderPageBuilder />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.MEMBERSHIP.STATISTICS}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <MembershipStatistics />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.REPORTS.ROOT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Reports />
              </>
            </CompanyGuard>
          }
        />
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.SALES.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Sales />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.SALES.ADD}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <SalesTransaction />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.SALES.VIEW}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <ViewSales />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.EXPENSES.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Expenses />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.EXPENSES.VIEW}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <ViewExpense />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.REPORTS.INVENTORY_REPORT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <InventoryReport />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path="/reports/bir-1701q"
          element={<Navigate to={ROUTES.REPORTS.ROOT} replace />}
        />
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.V2.SALES_ORDERS}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <SalesOrdersV2 />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.V2.SALES}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <SalesV2 />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.V2.PURCHASE_ORDERS}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <PurchaseOrdersV2 />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.V2.PURCHASES}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <PurchasesV2 />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.USER.COMPLETE_PROFILE}
          element={<CompleteProfile />}
        />
        <Route
          path={ROUTES.USER.CHANGE_PASSWORD}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <ChangePassword />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.USER.PROFILE}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <UserProfile />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.USER.SETTINGS}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <UserSettings />
              </>
            </CompanyGuard>
          }
        />
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.PRODUCTS.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Products />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.PRODUCTS.ADD}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <AddProduct />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.PRODUCTS.EDIT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <EditProduct />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.PRODUCTS.INVENTORY}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <ProductInventory />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.INVENTORY.ROOT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <InventoryList />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.INVENTORY.PRODUCT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <ProductInventory />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.INVENTORY.ADD}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <AddInventory />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.CUSTOMERS.ROOT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Customers />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.SUPPLIERS.ROOT}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Suppliers />
              </>
            </CompanyGuard>
          }
        />
        {betaEnabled && (
          <>
            <Route
              path={ROUTES.ROLE.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Role />
                  </>
                </CompanyGuard>
              }
            />
            <Route
              path={ROUTES.PERMISSION.ROOT}
              element={
                <CompanyGuard>
                  <>
                    <Sidebar />
                    <Header />
                    <Permission />
                  </>
                </CompanyGuard>
              }
            />
          </>
        )}
        <Route
          path={ROUTES.KANBAN}
          element={
            <CompanyGuard>
              <>
                <Sidebar />
                <Header />
                <Kanban />
              </>
            </CompanyGuard>
          }
        />
        <Route
          path={ROUTES.POS.ROOT}
          element={
            <CompanyGuard>
              <POSLayout />
            </CompanyGuard>
          }
        >
          <Route index element={<POS />} />
          <Route path="payment" element={<Payment />} />
          <Route path="success" element={<Success />} />
          <Route path="settings" element={<PrinterSettings />} />
          <Route path="printer-setup" element={<PrinterSetup />} />
        </Route>
        <Route
          path="*"
          element={
            <>
              <Sidebar />
              <Header />
              <NotFound />
            </>
          }
        />
      </Routes>
    </ProtectedRoute>
  );
}

export default AuthenticatedRoutes;
